import React, { useState } from 'react';
import { MocChange, ChangeStatus } from './types';
import { TablePagination, useTablePagination } from '../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../shared/useTableSort';

interface Props {
  changes: MocChange[];
  onSelectChange: (id: string) => void;
}

export const MocTable: React.FC<Props> = ({ changes, onSelectChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [deptFilter, setDeptFilter] = useState<string>('ALL');

  const filtered = changes.filter((c) => {
    const matchesSearch =
      c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.moc_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.target_department.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || c.status === statusFilter;
    const matchesDept = deptFilter === 'ALL' || c.target_department === deptFilter;
    return matchesSearch && matchesStatus && matchesDept;
  });

  const {
    sortedItems,
    sortField,
    sortDirection,
    requestSort,
  } = useTableSort(filtered);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    paginatedItems,
  } = useTablePagination(sortedItems, 25);

  const getStatusBadge = (status: ChangeStatus) => {
    switch (status) {
      case 'DRAFT':
      case 'SCREENING':
        return <span className="badge-pill badge-neutral">{status}</span>;
      case 'IMPACT_ASSESSMENT':
      case 'RISK_ASSESSMENT':
        return <span className="badge-pill badge-warning">{status.replace('_', ' ')}</span>;
      case 'APPROVAL':
        return <span className="badge-pill badge-info">Approval</span>;
      case 'IMPLEMENTATION':
        return <span className="badge-pill badge-info">Implementation</span>;
      case 'MONITORING':
      case 'PIR':
        return <span className="badge-pill badge-warning">{status}</span>;
      case 'CLOSED':
        return <span className="badge-pill badge-success">Closed</span>;
      case 'REJECTED':
      case 'CANCELLED':
        return <span className="badge-pill badge-danger">{status}</span>;
      default:
        return <span className="badge-pill badge-neutral">{status}</span>;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'CRITICAL':
        return <span className="badge-pill badge-danger">Critical</span>;
      case 'HIGH':
        return <span className="badge-pill badge-warning">High</span>;
      case 'MEDIUM':
        return <span className="badge-pill badge-info">Medium</span>;
      default:
        return <span className="badge-pill badge-neutral">{priority}</span>;
    }
  };

  const departments = Array.from(new Set(changes.map((c) => c.target_department)));

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Filters Bar */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '16px', background: '#f8fafc' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
          <input
            type="text"
            placeholder="Search MOC No, title, or department..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px 12px', background: '#ffffff', color: '#1e293b', width: '260px' }}
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px 10px', background: '#ffffff', color: '#1e293b' }}
          >
            <option value="ALL">All Statuses</option>
            <option value="DRAFT">DRAFT</option>
            <option value="SCREENING">SCREENING</option>
            <option value="IMPACT_ASSESSMENT">IMPACT ASSESSMENT</option>
            <option value="RISK_ASSESSMENT">RISK ASSESSMENT</option>
            <option value="APPROVAL">APPROVAL</option>
            <option value="IMPLEMENTATION">IMPLEMENTATION</option>
            <option value="PIR">PIR</option>
            <option value="CLOSED">CLOSED</option>
          </select>

          <select
            value={deptFilter}
            onChange={(e) => setDeptFilter(e.target.value)}
            style={{ fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px 10px', background: '#ffffff', color: '#1e293b' }}
          >
            <option value="ALL">All Departments</option>
            {departments.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>

        <span style={{ fontSize: '12px', color: '#64748b' }}>
          Showing: <strong style={{ color: '#0f172a' }}>{filtered.length}</strong> / {changes.length}
        </span>
      </div>

      {/* Table */}
      <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh field="moc_number" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                MOC No & Type
              </SortableTh>
              <SortableTh field="title" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Change Title & Department
              </SortableTh>
              <SortableTh field="priority" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Priority & Duration
              </SortableTh>
              <SortableTh field="status" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Status
              </SortableTh>
              <SortableTh field="target_completion_date" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Owner & Target Date
              </SortableTh>
              <SortableTh field="current_stage" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Stage Gates
              </SortableTh>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                  No MOC records found.
                </td>
              </tr>
            ) : (
              paginatedItems.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => onSelectChange(c.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0284c7', display: 'block' }}>{c.moc_number}</span>
                    <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '2px' }}>
                      {c.change_type} {c.execution_type === 'EMERGENCY' && '• 🚨 EMERGENCY'}
                    </span>
                  </td>
                  <td style={{ maxWidth: '340px' }}>
                    <span style={{ fontWeight: 600, color: '#0f172a', display: 'block' }}>{c.title}</span>
                    <span style={{ fontSize: '12px', color: '#64748b', display: 'block', marginTop: '2px' }}>
                      {c.target_department} {c.target_fleet_type && `• Fleet: ${c.target_fleet_type}`}
                    </span>
                  </td>
                  <td>
                    {getPriorityBadge(c.priority)}
                    <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '4px' }}>
                      {c.duration_type} {c.is_overdue && '• ⚠️ OVERDUE'}
                    </span>
                  </td>
                  <td>
                    {getStatusBadge(c.status)}
                  </td>
                  <td>
                    <span style={{ fontSize: '12px', fontWeight: 500, color: '#1e293b', display: 'block' }}>{c.change_owner_name}</span>
                    <span style={{ fontSize: '11px', color: '#64748b', display: 'block', fontFamily: 'monospace', marginTop: '2px' }}>
                      {c.planned_end_date ? `End: ${c.planned_end_date}` : 'No date'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span
                        title="Impact Assessment"
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '4px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          fontWeight: 700,
                          background: c.impact_assessment_completed ? '#dcfce7' : '#f1f5f9',
                          color: c.impact_assessment_completed ? '#15803d' : '#94a3b8',
                        }}
                      >
                        I
                      </span>
                      <span
                        title="Risk Assessment"
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '4px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          fontWeight: 700,
                          background: c.risk_assessment_completed ? '#dcfce7' : '#f1f5f9',
                          color: c.risk_assessment_completed ? '#15803d' : '#94a3b8',
                        }}
                      >
                        R
                      </span>
                      <span
                        title="Approval Granted"
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '4px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          fontWeight: 700,
                          background: c.approval_granted ? '#dcfce7' : '#f1f5f9',
                          color: c.approval_granted ? '#15803d' : '#94a3b8',
                        }}
                      >
                        A
                      </span>
                      <span
                        title="PIR Completed"
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '4px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          fontWeight: 700,
                          background: c.pir_completed ? '#dcfce7' : '#f1f5f9',
                          color: c.pir_completed ? '#15803d' : '#94a3b8',
                        }}
                      >
                        P
                      </span>
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectChange(c.id);
                      }}
                      className="btn-secondary"
                      style={{ width: 'auto', padding: '5px 12px', fontSize: '12px' }}
                    >
                      Detail →
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <TablePagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        itemLabel="changes"
      />
    </div>
  );
};
